import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  CircularProgress,
  Typography,
  Checkbox,
  Chip,
  InputAdornment
} from "@material-ui/core";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import { Formik } from "formik";
import DateFnsUtils from "@date-io/date-fns";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import ptBrLocale from "date-fns/locale/pt-BR";
import styled from "styled-components";
import moment from "moment";
import _ from "lodash";
import Dialog from "../../../waybee-ui/Dialog/Dialog";
import Heading from "../../../waybee-ui/Heading/Heading";
import Button from "../../../waybee-ui/Button";
import TeacherService from "../../../services/TeacherService";
import decodeBase64 from "../../../utils/decodeBase64";

const FormGrid = styled(Grid)`
  overflow: scroll;

  @media (min-height: 800px) {
    scrollbar-width: none;
  }
`;

const Content = styled(DialogContent)`
  overflow: hidden;
`;

const AttachmentText = styled(Typography)`
  margin-top: 20px;
`;

const AcknowledgeCheckbox = styled(Checkbox)`
  padding: 0;
`;

const AcknowledgeTextField = styled(TextField)`
  .MuiInput-underline:before {
    border-bottom: none;
  }
  .MuiInput-underline:after {
    border-bottom: none;
  }
  .MuiInput-underline:hover:not(.Mui-disabled):before {
    border-bottom: none;
  }
`;

const OccurrenceDialog = ({ open, handleClose, occurrence, studentId }) => {
  const [loading, setLoading] = useState(false);
  const [occurFiles, setOccurFiles] = useState(
    occurrence.occurrenceAttachment ? occurrence.occurrenceAttachment : []
  );
  const stringDate = new Date(occurrence.date)
    .toLocaleString("pt-BR")
    .split(",");
  const formatData = (data, deleteOccurrence) => {
    const files = [];

    occurFiles.map(occurFile =>
      files.push({
        name: occurFile.attachmentName,
        content: occurFile.attachmentContent
      })
    );

    return {
      absences: [],
      idTurmaDisc: occurrence.idTurma,
      idPerlet: occurrence.idPerlet,
      date: moment(data.date).toISOString(),
      occurrences: deleteOccurrence
        ? []
        : [
            {
              description: data.obs,
              idGroup: occurrence.idGroup,
              idType: occurrence.idType,
              files,
              studentId
            }
          ],
      removed: [
        {
          description: occurrence.description,
          id: `${occurrence.idType}-${occurrence.idGroup}`,
          idOccur: occurrence.id,
          studentId: occurrence.studentId
        }
      ]
    };
  };

  const insertOccurrence = async (data, deleteOccurrence) => {
    const occurData = formatData(data, deleteOccurrence);
    setLoading(true);
    await TeacherService.saveOccurrence(occurData).then(() =>
      handleClose(true)
    );
  };

  const deleteAttachment = occurFile => {
    const files = _.filter(
      occurFiles,
      file =>
        file.attachmentName !== occurFile.attachmentName &&
        file.attchmentContent !== occurFile.attachmentContent
    );
    setOccurFiles(files);
  };

  const handleFileSelect = (e, setFieldValue) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const reader = new FileReader();

        reader.onload = event => {
          const fileData = {
            id: file.lastModified + file.size,
            name: file.name,
            content: event.target.result.split(",")[1]
          };

          occurFiles.push({
            attachmentName: fileData.name,
            attachmentContent: fileData.content
          });

          if (occurFiles) {
            setFieldValue("attachmentName", [...occurFiles, fileData.name]);
            setFieldValue("attachmentData", [...occurFiles, fileData.content]);
          } else {
            setFieldValue("attachmentName", [fileData.name]);
            setFieldValue("attachmentData", [fileData.content]);
          }
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleOnOpenFile = file => {
    const occurrenceBase64 = file.attachmentContent;
    const decodedUrl = decodeBase64(occurrenceBase64);

    const downloadLink = document.createElement("a");
    downloadLink.href = decodedUrl;
    downloadLink.download = file.attachmentName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth closeButton>
      <DialogTitle>Ocorrência</DialogTitle>
      <Formik
        initialValues={{
          obs: occurrence.description,
          type: occurrence.reason,
          stage: occurrence.stage,
          teacher: occurrence.teacher,
          course: occurrence.courseName,
          date: occurrence.date,
          occurrenceAttachments: occurrence.occurrenceAttachment,
          responsibleAcknowledge: occurrence.responsibleAcknowledge,
          responsibleName: occurrence.responsibleName
        }}
      >
        {({ values, handleChange, setFieldValue }) => (
          <FormGrid>
            <Content>
              <Heading>{`Ocorrência do dia ${stringDate[0]} às ${stringDate[1]} - Etapa ${occurrence.stage}`}</Heading>
              <Grid container>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <AcknowledgeTextField
                      name="responsibleAcknowledge"
                      id="responsibleAcknowledge"
                      label="Ciente"
                      value={
                        values.responsibleAcknowledge === "S"
                          ? values.responsibleName
                          : "Não houve ciência"
                      }
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AcknowledgeCheckbox
                              checked={values.responsibleAcknowledge === "S"}
                              disabled
                            />
                          </InputAdornment>
                        ),
                        style: {
                          opacity: 0.5,
                          pointerEvents: "none"
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="stage"
                      id="stage"
                      label="Etapa"
                      value={values.stage}
                      fullWidth
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      name="type"
                      id="type"
                      label="Tipo"
                      value={values.type}
                      fullWidth
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      name="teacher"
                      id="teacher"
                      label="Professor"
                      value={values.teacher}
                      fullWidth
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      name="course"
                      id="course"
                      label="Disciplina"
                      value={values.course}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <MuiPickersUtilsProvider
                      utils={DateFnsUtils}
                      locale={ptBrLocale}
                    >
                      <DatePicker
                        name="date"
                        id="date"
                        label="Data"
                        value={values.date}
                        fullWidth
                        format="dd/MM/yyyy"
                        onChange={value => {
                          setFieldValue("date", value);
                        }}
                        disabled={!occurrence.editable}
                      />
                    </MuiPickersUtilsProvider>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      editable={occurrence.editable}
                      onChange={handleChange}
                      name="obs"
                      id="obs"
                      label="Observações"
                      value={values.obs}
                      fullWidth
                      rowsMax="7"
                      multiline
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <label htmlFor="files-container">
                      <Button
                        variant="contained"
                        color="primary"
                        component="span"
                        startIcon={<AttachFileIcon />}
                        fullwidth
                      >
                        Adicionar anexo
                      </Button>
                      <input
                        accept="*"
                        id="files-container"
                        name="files-container"
                        multiple
                        type="file"
                        style={{ display: "none" }}
                        onChange={e => {
                          handleFileSelect(e, setFieldValue);
                          e.target.value = null;
                        }}
                      />
                    </label>
                    {occurFiles.length !== 0 && (
                      <>
                        <AttachmentText variant="body1" color="textSecondary">
                          Anexos:
                        </AttachmentText>
                        {occurFiles.map(attch => (
                          <Chip
                            onDelete={() => {
                              deleteAttachment(attch);
                            }}
                            onClick={() => handleOnOpenFile(attch)}
                            key={attch.attachmentContent}
                            icon={<AttachFileIcon />}
                            label={attch.attachmentName}
                          />
                        ))}
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Content>
            <DialogActions>
              {loading ? (
                <CircularProgress />
              ) : (
                <>
                  {occurrence.editable && (
                    <Button
                      variant="contained"
                      color="default"
                      onClick={() => insertOccurrence(values, true)}
                    >
                      Excluir
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      occurrence.editable
                        ? insertOccurrence(values)
                        : handleClose}
                  >
                    {occurrence.editable ? "Salvar" : "Ok"}
                  </Button>
                </>
              )}
            </DialogActions>
          </FormGrid>
        )}
      </Formik>
    </Dialog>
  );
};

OccurrenceDialog.propTypes = {
  handleClose: PropTypes.func.isRequired,
  occurrence: PropTypes.shape({
    courseName: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    reason: PropTypes.string.isRequired,
    stage: PropTypes.string.isRequired,
    teacher: PropTypes.string.isRequired,
    editable: PropTypes.string.isRequired,
    idType: PropTypes.string.isRequired,
    idGroup: PropTypes.string.isRequired,
    idPerlet: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    idTurma: PropTypes.string.isRequired,
    studentId: PropTypes.string,
    occurrenceAttachment: PropTypes.string,
    attachmentName: PropTypes.string,
    responsibleAcknowledge: PropTypes.string,
    responsibleName: PropTypes.string
  }).isRequired,
  studentId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired
};

export default OccurrenceDialog;
